import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useEffect, useMemo, useState } from 'react'
import { useFarmContext } from 'src/Elements/Farm'
import { useTranslatedSystemlogOptions } from 'src/Libs/installations/hooks/use-translated-systemlog-options'
import { MultiSelectPlaceholderOptions, PlaceholderOptions } from 'src/Types/globalTypes'
import { SystemLogFilter, SystemlogResolvedType, SystemlogSort, SystemlogType } from '../types'

export const useSystemlogFilterOptions = (storageId?: string): SystemLogFilter => {
  const { isFarmMasterSelected, farmClients } = useFarmContext()
  const { t } = useCustomTranslation()
  const { translatedTypeOptions, translatedStatusOptions, translatedSortOptions } = useTranslatedSystemlogOptions()

  const typeOptions: MultiSelectPlaceholderOptions<SystemlogType> = {
    ...translatedTypeOptions,
    activeValue: [],
    size: 'small',
    fieldName: 'errorType',
  }

  const statusOptions: MultiSelectPlaceholderOptions<SystemlogResolvedType> = {
    ...translatedStatusOptions,
    activeValue: [],
    size: 'small',
    fieldName: 'isResolved',
  }

  const sortOptions: PlaceholderOptions<SystemlogSort> = {
    ...translatedSortOptions,
    activeValue: SystemlogSort.Descending,
    size: 'small',
    fieldName: 'createdAt',
  }

  const clientActiveValue = useMemo(() => {
    const value: number[] = []

    if (!isFarmMasterSelected) {
      value.push(Number(storageId))
    }

    return value
  }, [isFarmMasterSelected, storageId])

  const clientOptions: MultiSelectPlaceholderOptions<number> = useMemo(
    () => ({
      placeholder: t('systemlog.clientFilter'),
      options: farmClients?.map(client => ({ label: `${client.storageId}`, value: Number(client.storageId) })) ?? [],
      activeValue: clientActiveValue,
      size: 'small',
      fieldName: 'deviceLinkId',
    }),
    [farmClients, clientActiveValue, t]
  )

  const [typeFilter, setTypeFilter] = useState<MultiSelectPlaceholderOptions<SystemlogType>>(typeOptions)
  const [statusFilter, setStatusFilter] = useState<MultiSelectPlaceholderOptions<SystemlogResolvedType>>(statusOptions)
  const [sortFilter, setSortFilter] = useState<PlaceholderOptions<SystemlogSort>>(sortOptions)
  const [clientFilter, setClientFilter] = useState<MultiSelectPlaceholderOptions<number>>(clientOptions)

  useEffect(() => {
    setClientFilter(clientOptions)
  }, [clientOptions])

  const setTypeFilterValue = (activeValue: SystemlogType[]): void => {
    setTypeFilter(state => ({ ...state, activeValue }))
  }

  const setStatusFilterValue = (activeValue: SystemlogResolvedType[]): void => {
    setStatusFilter(state => ({ ...state, activeValue }))
  }

  const setSortFilterValue = (activeValue: SystemlogSort): void => {
    setSortFilter(state => ({ ...state, activeValue }))
  }

  const setClientFilterValue = (activeValue: number[]): void => {
    setClientFilter(state => ({ ...state, activeValue }))
  }

  return {
    typeFilter,
    setTypeFilterValue,
    statusFilter,
    setStatusFilterValue,
    sortFilter,
    setSortFilterValue,
    clientFilter,
    setClientFilterValue,
  }
}
